import { useEffect, useState } from "react";
import { Landmark, Sparkles, Loader2, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MicButton from "@/components/ui/MicButton";
import SafeBlock from "@/components/ui/SafeBlock";
import ReportShell from "@/components/reports/ReportShell";
import ReportVersionsBar from "@/components/reports/ReportVersionsBar";
import PrevisionnelSelect from "@/components/memo/PrevisionnelSelect";
import { startArreteJob, fetchArreteJob, fetchLastArrete, downloadArretePdf } from "@/lib/arrete_plan";

const KIND = "arrete_plan";

export default function ArretePlanSection({ dossierId }) {
    const [titre, setTitre] = useState("4 - Arrêté du Plan");
    const [dateAudience, setDateAudience] = useState("");
    const [regime, setRegime] = useState("");
    const [prevId, setPrevId] = useState("");
    const [briefing, setBriefing] = useState("");
    const [content, setContent] = useState("");
    const [sessionId, setSessionId] = useState(null);
    const [generatedAt, setGeneratedAt] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [stage, setStage] = useState("");
    const [exporting, setExporting] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        fetchLastArrete(dossierId).then((r) => {
            if (r) { setContent(r.content || ""); setSessionId(r.session_id); setGeneratedAt(r.created_at); if (r.titre) setTitre(r.titre); if (r.date_audience) setDateAudience(r.date_audience); if (r.briefing) setBriefing(r.briefing); if (r.regime) setRegime(r.regime); }
        }).catch(() => {});
    }, [dossierId]);

    function loadVersion(d) {
        if (!d) { setContent(""); setSessionId(null); setGeneratedAt(null); return; }
        setContent(d.content || ""); setSessionId(d.session_id); setGeneratedAt(d.created_at);
        if (d.titre) setTitre(d.titre); if (d.date_audience) setDateAudience(d.date_audience); if (d.briefing) setBriefing(d.briefing); if (d.regime) setRegime(d.regime);
    }

    async function generate() {
        if (generating) return;
        setGenerating(true); setContent(""); setSessionId(null); setStage("preparation");
        try {
            const { job_id } = await startArreteJob(dossierId, { titre: titre.trim() || "4 - Arrêté du Plan", date_audience: dateAudience || null, briefing: briefing || null, regime: regime || null, previsionnel_id: prevId || null });
            for (;;) {
                await new Promise((r) => setTimeout(r, 3000));
                const j = await fetchArreteJob(dossierId, job_id);
                setStage(j.stage);
                if (j.stage === "done") { setContent(j.content || ""); setSessionId(j.session_id); setGeneratedAt(new Date().toISOString()); setRefreshKey((k) => k + 1); toast.success("Rapport « Arrêté du Plan » généré"); break; }
                if (j.stage === "error") { toast.error("Erreur IA", { description: String(j.error), duration: 12000 }); break; }
            }
        } catch (e) { toast.error("Génération impossible", { description: e.message }); }
        finally { setGenerating(false); setStage(""); }
    }

    async function exportPdf() {
        setExporting(true);
        try { await downloadArretePdf(dossierId, sessionId); toast.success("PDF téléchargé"); }
        catch (e) { toast.error("Export PDF impossible", { description: e.message }); }
        finally { setExporting(false); }
    }

    return (
        <section data-testid="arrete-plan-section" className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center"><Landmark className="h-4 w-4 text-primary-foreground" strokeWidth={1.75} /></div>
                    <div>
                        <h3 className="font-display text-xl text-foreground">4 - Arrêté du Plan (Plan de Continuation · 10 ans)</h3>
                        <p className="text-xs text-muted-foreground">Document officiel du plan (Sauvegarde / Redressement) pour le Tribunal · intègre la projection d&apos;apurement 10 ans saisie dans l&apos;onglet Prévisionnels.</p>
                    </div>
                </div>
                <Button type="button" data-testid="arrete-generate" onClick={generate} disabled={generating} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
                    {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {generating ? (stage === "generation" ? "Rédaction du plan…" : "Lecture du dossier…") : "Générer le rapport"}
                </Button>
            </div>

            <Card className="border-slate-200 bg-white shadow-sm"><CardContent className="pt-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                        <label className="text-sm text-muted-foreground" htmlFor="arrete-titre">Titre du rapport</label>
                        <Input id="arrete-titre" data-testid="arrete-titre" value={titre} onChange={(e) => setTitre(e.target.value)} disabled={generating} className="h-10 bg-background" placeholder="4 - Arrêté du Plan" />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-sm text-muted-foreground" htmlFor="arrete-date">Date d&apos;audience</label>
                        <Input id="arrete-date" type="date" data-testid="arrete-date" value={dateAudience} onChange={(e) => setDateAudience(e.target.value)} disabled={generating} className="h-10 bg-background" />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-sm text-muted-foreground">Type de plan</label>
                        <Select value={regime} onValueChange={setRegime} disabled={generating}>
                            <SelectTrigger data-testid="arrete-regime" className="h-10 bg-background"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="sauvegarde" data-testid="arrete-regime-sauvegarde">Plan de Sauvegarde</SelectItem>
                                <SelectItem value="redressement" data-testid="arrete-regime-redressement">Plan de Redressement</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <PrevisionnelSelect dossierId={dossierId} value={prevId} onChange={setPrevId} disabled={generating} testId="arrete-previsionnel-ref" />
                <div className="space-y-1.5">
                    <label className="text-sm text-muted-foreground" htmlFor="arrete-briefing">Briefing (consignes à intégrer)</label>
                    <div className="flex items-start gap-2">
                        <Textarea id="arrete-briefing" data-testid="arrete-briefing" value={briefing} onChange={(e) => setBriefing(e.target.value)} disabled={generating} placeholder="Points à mettre en avant : bilan de la période d'observation, mesures sociales, engagements des créanciers, garanties du plan…" className="bg-background resize-y text-sm min-h-[240px]" />
                        <MicButton testId="arrete-briefing-mic" onText={(t) => setBriefing((v) => (v ? v + " " : "") + t)} />
                    </div>
                </div>
            </CardContent></Card>

            {(content || generating) && (
                <Card data-testid="arrete-result" className="border-slate-200 bg-white shadow-sm"><CardContent className="pt-6 space-y-3">
                    <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" /><h3 className="font-display text-lg text-foreground">{titre || "4 - Arrêté du Plan"}</h3>
                        <ReportVersionsBar dossierId={dossierId} kind={KIND} currentSessionId={sessionId} onLoad={loadVersion} refreshKey={refreshKey} disabled={generating} />
                    </div>
                    <SafeBlock label="Le rapport"><ReportShell dossierId={dossierId} sessionId={sessionId} kind={KIND} content={content} onContentChange={setContent} charts={[]} onChartsChange={() => {}} streaming={generating} /></SafeBlock>
                    {!content && <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Génération en cours…</p>}
                    {content && !generating && (
                        <div className="flex justify-end border-t border-slate-200 pt-4">
                            <Button type="button" variant="outline" data-testid="arrete-export-pdf" onClick={exportPdf} disabled={exporting} className="rounded-full">{exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Télécharger PDF</Button>
                        </div>
                    )}
                </CardContent></Card>
            )}
        </section>
    );
}
